import { useContext } from "react";
import { Button, Input, Modal, Select, Table, Tag, Typography } from "antd";
import {
    PlusOutlined,
    SearchOutlined,
    TeamOutlined,
} from "@ant-design/icons";

import usePanelUsers from "./usePanelUsers";
import PageHeader from "../../components/PageHeader";
import ActiveFiltersBar from "../../components/ActiveFiltersBar";
import TableCard from "../../components/TableCard";
import TableDicaHint from "../../components/TableDicaHint";
import TablePagination from "../../components/TablePagination";
import { ThemeContext } from "../../contexts/ThemeContext";
import { poeValorComPontosEmMil } from "../../utils/Conversores";

const { Paragraph } = Typography;

export default function PanelUsers() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const {
        isLoading,
        isLoadingUpload,
        isUpdateActiveModalVisible,
        panelUserBeingUpdated,
        columns,
        tableData,
        filteredData,
        roles,
        cities,
        searchText,
        setSearchText,
        filterRole,
        setFilterRole,
        filterActive,
        setFilterActive,
        filterCity,
        setFilterCity,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        activeFilterItems,
        handleClickCreateNewPanelUser,
        handleClickEditPanelUser,
        handleConfirmUpdateActivePanel,
        handleCancelUpdateActivePanel,
        handleClearAllFilters,
    } = usePanelUsers();

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Page Header */}
            <PageHeader
                icon={<TeamOutlined />}
                iconBg="linear-gradient(135deg, #1677ff 0%, #0958d9 100%)"
                title="Usuários do Painel"
                tag={<Tag color="blue">{poeValorComPontosEmMil(tableData.length)} cadastrados</Tag>}
                subtitle="Gerencie os usuários administrativos, seus níveis de permissão e municípios vinculados"
                actions={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleClickCreateNewPanelUser}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            fontWeight: 600,
                            height: 38,
                            borderRadius: 8,
                        }}
                    >
                        Novo Usuário
                    </Button>
                }
            />

            {/* Filter Bar / Controls */}
            <div
                style={{
                    background: isDark ? "#141414" : "#ffffff",
                    border: `1px solid ${isDark ? "#262626" : "#e2e8f0"}`,
                    borderRadius: 10,
                    padding: "12px 16px",
                    display: "flex",
                    alignItems: "flex-end",
                    flexWrap: "wrap",
                    gap: 14,
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 220, maxWidth: 300 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#d4d4d4" : "#475569" }}>
                        Pesquisar Usuário
                    </span>
                    <Input
                        placeholder="Buscar por nome ou e-mail..."
                        prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                        value={searchText}
                        onChange={(e) => {
                            setSearchText(e.target.value);
                            setCurrentPage(1);
                        }}
                        allowClear
                        style={{ borderRadius: 8, height: 36 }}
                    />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 180, flex: 1, maxWidth: 220 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#d4d4d4" : "#475569" }}>
                        Perfil de Acesso
                    </span>
                    <Select
                        value={filterRole}
                        onChange={(val) => {
                            setFilterRole(val);
                            setCurrentPage(1);
                        }}
                        style={{ width: "100%", height: 36 }}
                        options={[
                            { value: "ALL", label: "Todos os Perfis" },
                            ...roles.map((r) => ({ value: r.id!, label: r.name })),
                        ]}
                    />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 140, flex: 1, maxWidth: 160 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#d4d4d4" : "#475569" }}>
                        Status do Usuário
                    </span>
                    <Select
                        value={filterActive}
                        onChange={(val) => {
                            setFilterActive(val);
                            setCurrentPage(1);
                        }}
                        style={{ width: "100%", height: 36 }}
                        options={[
                            { value: "ALL", label: "Todos os Status" },
                            { value: "ACTIVE", label: "Ativos" },
                            { value: "INACTIVE", label: "Inativos" },
                        ]}
                    />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 200, flex: 1, maxWidth: 260 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#d4d4d4" : "#475569" }}>
                        Município Vinculado
                    </span>
                    <Select
                        value={filterCity}
                        onChange={(val) => {
                            setFilterCity(val);
                            setCurrentPage(1);
                        }}
                        showSearch
                        filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: "100%", height: 36 }}
                        options={[
                            { value: "ALL", label: "Todos os Municípios" },
                            ...cities.map((c) => ({
                                value: c.id!,
                                label: `${c.city} - ${c.uf}`,
                            })),
                        ]}
                    />
                </div>
            </div>

            {/* Active Filters Bar */}
            <ActiveFiltersBar
                filters={activeFilterItems}
                onClearAll={handleClearAllFilters}
            />

            {/* Hint & Pagination (Top) */}
            <TableDicaHint text="Clique em uma linha para editar o usuário ou use as ações para ativar/inativar o acesso." />

            <TablePagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredData.length}
                itemName="usuários"
                pageSizeOptions={["10", "25", "50", "100"]}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                }}
            />

            {/* Table Card */}
            <TableCard>
                <Table
                    columns={columns as any}
                    dataSource={filteredData.slice(
                        (currentPage - 1) * pageSize,
                        currentPage * pageSize
                    )}
                    rowKey="key"
                    loading={isLoading}
                    pagination={false}
                    className={`modern-data-table ${isDark ? "modern-data-table-dark" : ""}`}
                    scroll={{ x: 750 }}
                    onRow={(record) => ({
                        onClick: () => handleClickEditPanelUser(record),
                        style: { cursor: "pointer" },
                    })}
                />
            </TableCard>

            {/* Pagination (Bottom) */}
            <TablePagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredData.length}
                itemName="usuários"
                pageSizeOptions={["10", "25", "50", "100"]}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                }}
            />

            {/* Activate / Inactivate Confirmation Modal */}
            <Modal
                open={isUpdateActiveModalVisible}
                onOk={handleConfirmUpdateActivePanel}
                onCancel={handleCancelUpdateActivePanel}
                confirmLoading={isLoadingUpload}
                okText={panelUserBeingUpdated?.active ? "Sim, Inativar" : "Sim, Ativar"}
                cancelText="Cancelar"
                okType={panelUserBeingUpdated?.active ? "danger" : "primary"}
                okButtonProps={{
                    loading: isLoadingUpload,
                    danger: Boolean(panelUserBeingUpdated?.active),
                }}
                cancelButtonProps={{ disabled: isLoadingUpload }}
                centered
                title={
                    panelUserBeingUpdated?.active
                        ? "Confirmar Inativação de Usuário"
                        : "Confirmar Ativação de Usuário"
                }
            >
                <Paragraph style={{ marginTop: 12 }}>
                    Deseja realmente {panelUserBeingUpdated?.active ? "inativar" : "ativar"} o usuário{" "}
                    <strong>"{panelUserBeingUpdated?.name}"</strong>?
                </Paragraph>
                <div
                    style={{
                        background: isDark ? "#141414" : panelUserBeingUpdated?.active ? "#fff1f0" : "#f6ffed",
                        border: `1px solid ${
                            isDark
                                ? "#262626"
                                : panelUserBeingUpdated?.active
                                ? "#ffa39e"
                                : "#b7eb8f"
                        }`,
                        borderRadius: 6,
                        padding: "10px 14px",
                        fontSize: 12.5,
                        color: isDark
                            ? "#e2e8f0"
                            : panelUserBeingUpdated?.active
                            ? "#cf1322"
                            : "#389e0d",
                    }}
                >
                    {panelUserBeingUpdated?.active
                        ? "Ao inativar este usuário, ele perderá imediatamente o acesso a todas as rotas do painel administrativo."
                        : "Ao ativar este usuário, ele poderá realizar login e acessar os módulos conforme as permissões de seu perfil."}
                </div>
            </Modal>
        </div>
    );
}
